import {proxy, subscribe, useSnapshot} from 'valtio';
import {
  GetNextBuyflowStepResponse,
  ProductWidget,
  StreamingServicesProductWidget,
} from '@/lib/TypesOpenApi';
import {StepWidgetStore, StepsPath, stepsMap} from '../routing/StepsRouting';
import {useWidget} from '../routing/StepsWizardContext';
import {persist} from '@/lib/vatio-persistence';
import {DeepReadonly} from '@/lib/typings/readonly';
import {streaming, toggleStreamingService} from './CartStore';
import {getAnalytics, useAnalytics} from '@/hooks/useAnalytics';
import {useCallback} from 'react';
import {getProductWidget} from './widget';

const widget = stepsMap['STREAMING']!.store;

/**
 * Represeent productId values that hardcoded on BFF side
 * Will be kept the same on all environments as hack by BFF team decision: https://gfiber.atlassian.net/browse/GFENG-17382
 * */
export enum StreamingChoiceIds {
  NoStreaming = 'noStreaming',
  StreamingEnabled = 'service1',
}

export interface StreamingPageWidget {
  /** Base widget to relay on */
  widget: StepWidgetStore;
  content?: GetNextBuyflowStepResponse;
  productWidget?: StreamingServicesProductWidget;
}

export const streamingWidget = proxy<StreamingPageWidget>({
  widget,
  get content() {
    return this.widget.content;
  },

  get productWidget() {
    return getProductWidget(
      this.content,
      'streamingServicesProductWidget',
    ) as StreamingServicesProductWidget;
  },
});

// widget data expected to be loaded on previus page
export const useStreamingWidget = () =>
  useWidget(StepsPath.Streaming, streamingWidget);

export interface StreamingState {
  selected?: ProductWidget;
  productId?: string;
  /**
   * Indicates whether streaming enabled or not.
   * @example: When choose "Add streaming" option card, value will be equal true
   * */
  isEnabled?: boolean;
  expandedProducts: string[];
}

// if add any new field, add the new field to the below reset function also
export const store = persist<StreamingState>(StepsPath.Streaming, {
  selected: undefined,
  // isEnabled depend on widget, so will set value directly instead of using getter
  // it allow to persist value between sessions
  isEnabled: undefined,
  get productId() {
    return this.selected?.productId;
  },
  expandedProducts: [],
});

export const resetStreamingState = () => {
  store.selected = undefined;
  store.isEnabled = undefined;
  store.expandedProducts = [];
};

export const useStreamingState = () => useSnapshot(store);

// we must preselect "No Streaming" option when user open streaming page first time
// but we not know exact product id and product itself before we load it
export const preselectNoStreamingOption =
  (widget: StreamingPageWidget) => () => {
    // if we first time loads page, then probably product will be undefined
    if (store.selected) {
      return;
    }

    // Try preselect "No Streaming" option
    const product = widget.productWidget?.productWidgets.filter(
      product => product.isDefaultSelection,
    )[0];
    if (!product) {
      // avoid unnecesary assigns to decrease chance of re-rendering
      return;
    }

    store.selected = product;

    const analytics = getAnalytics();
    analytics.trackStreamingCardSelection('', product.productId);
  };

subscribe(streamingWidget, preselectNoStreamingOption(streamingWidget));

/**
 * Applies deafult services selection.
 * Applies only for first model update, such as after that user selection has priority
 */
export const preselectSerivices = (widget: StreamingPageWidget) => () => {
  if (streaming.services.length) {
    return;
  }

  const preselectedServices =
    widget.productWidget?.drawerWidget?.streamingServices.filter(
      ({isDefaultSelection}) => isDefaultSelection,
    ) || [];

  preselectedServices.map(p =>
    toggleStreamingService(p, p.productCost?.units ?? 0),
  );
};

subscribe(streamingWidget.widget, preselectSerivices(streamingWidget));

export const useSetStreamingSelection = () => {
  const analytics = useAnalytics();

  return useCallback(
    (select?: DeepReadonly<ProductWidget>) => {
      if (store.selected?.productId !== select?.productId) {
        analytics.trackStreamingCardSelection(
          store.selected?.productId,
          select?.productId,
        );
      }

      store.selected = select as ProductWidget;
      if (!select) {
        store.isEnabled = undefined;
        return;
      }

      store.isEnabled =
        select.productId === StreamingChoiceIds.StreamingEnabled;
    },
    [analytics],
  );
};

export const toggleExpanded = (productId: string) => {
  if (store.expandedProducts.includes(productId)) {
    store.expandedProducts = store.expandedProducts.filter(
      x => x !== productId,
    );
    return;
  }

  store.expandedProducts.push(productId);
};
